import type { HTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/utils/cn'

const badgeVariants = cva(
  // Chips are always one line: nowrap, never wider than their cell (text
  // ellipsizes with the full label as a tooltip — see Badge below).
  'inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[10px] font-medium leading-none transition-colors sm:gap-1.5 sm:px-2.5 sm:text-[11px]',
  {
    variants: {
      variant: {
        default:
          'border-primary/20 bg-primary/10 text-primary dark:bg-primary/20',
        secondary:
          'border-border bg-secondary text-secondary-foreground',
        outline: 'border-border bg-card text-foreground',
        success:
          'border-emerald-300/80 bg-emerald-100 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/20 dark:text-emerald-300',
        warning:
          'border-amber-300/80 bg-amber-100 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/20 dark:text-amber-300',
        destructive:
          'border-rose-300/80 bg-rose-100 text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/20 dark:text-rose-300',
        info:
          'border-sky-300/80 bg-sky-100 text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/20 dark:text-sky-300',
        progress:
          'border-violet-300/80 bg-violet-100 text-violet-800 dark:border-violet-500/40 dark:bg-violet-500/20 dark:text-violet-300',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

const dotColors: Record<string, string> = {
  default: 'bg-primary',
  secondary: 'bg-muted-foreground',
  outline: 'bg-muted-foreground',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  destructive: 'bg-red-500',
  info: 'bg-blue-500',
  progress: 'bg-indigo-500',
}

export interface BadgeProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  dot?: boolean
}

function Badge({ className, variant = 'default', dot = true, children, title, ...props }: BadgeProps) {
  const text = typeof children === 'string' || typeof children === 'number' ? String(children) : null
  return (
    <div className={cn(badgeVariants({ variant }), className)} title={title ?? text ?? undefined} {...props}>
      {dot ? (
        <span
          className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotColors[variant ?? 'default'])}
          aria-hidden
        />
      ) : null}
      {text != null ? <span className="min-w-0 truncate">{text}</span> : children}
    </div>
  )
}

export { Badge, badgeVariants }
